import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from "react";
import { useAuth } from "../auth";
import { useAccountData } from "../hooks/useAccountData";
import { useIdentityProfile } from "../hooks/useIdentityProfile";
import { useIdpEnvironment } from "../hooks/useIdpEnvironment";
import { getActiveAccount } from "../utils/get-active-account";
import { extractProductCodesFromAccountContracts } from "./helpers/account-extractors";
import { usePendoScript } from "./usePendoScript";
import { buildAccountConfig, buildVisitorConfig } from "./utils";
/**
 * Internal component that handles Pendo initialization logic
 * This component is only rendered when pendoKey is valid
 */
const PendoInitializer = ({ children, config, }) => {
    const { user, roles, isLoading: isAuthLoading } = useAuth();
    const idpEnv = useIdpEnvironment();
    // Determine account ID: use provided accountId or get the active account
    // Memoized to prevent unnecessary calls to getActiveAccount() and re-queries
    const accountId = useMemo(() => { var _a; return (_a = config.accountId) !== null && _a !== void 0 ? _a : getActiveAccount(); }, [config.accountId]);
    const { accountData, isLoading: isAccountDataLoading } = useAccountData(accountId);
    const { identityProfile, isLoading: isIdentityProfileLoading } = useIdentityProfile();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const productCodes = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    () => extractProductCodesFromAccountContracts(accountData), [accountData]);
    const isPendoLoaded = usePendoScript(config.pendoKey);
    // Initialize Pendo when all data is ready
    useEffect(() => {
        var _a, _b;
        const isWindowAvailable = typeof window !== "undefined";
        const isReady = Boolean(user) &&
            roles.length > 0 &&
            !isAuthLoading &&
            !isAccountDataLoading &&
            !isIdentityProfileLoading &&
            isPendoLoaded &&
            isWindowAvailable &&
            window.pendo !== undefined;
        if (!isReady || !user || !window.pendo)
            return;
        const visitor = buildVisitorConfig(user, roles, identityProfile, idpEnv, config, accountId);
        const account = buildAccountConfig(accountId, accountData, productCodes, idpEnv, config);
        const initConfig = Object.assign({ visitor,
            account }, config.pendoOptions);
        try {
            (_b = (_a = window.pendo).initialize) === null || _b === void 0 ? void 0 : _b.call(_a, initConfig);
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error("PendoProvider: Failed to initialize Pendo", error);
        }
    }, [
        user,
        roles,
        isAuthLoading,
        isAccountDataLoading,
        isIdentityProfileLoading,
        isPendoLoaded,
        accountId,
        accountData,
        identityProfile,
        productCodes,
        idpEnv,
        config,
    ]);
    return _jsx(_Fragment, { children: children });
};
/**
 * PendoProvider - Reusable provider for Pendo analytics
 *
 * @param config.pendoKey - Pendo API key (REQUIRED) - Provider will not initialize without it
 * @param config.appEnv - Application environment (REQUIRED) - Provider will not initialize without it
 * @param config.accountId - Account ID for account data (OPTIONAL)
 * @param config.customVisitorAttributes - Custom visitor attributes (OPTIONAL)
 * @param config.customAccountAttributes - Custom account attributes (OPTIONAL)
 *
 */
export const PendoProvider = ({ children, config, }) => {
    // Allowed appEnv values
    const allowedAppEnvs = ["dev", "stg", "prd", "load", "demo"];
    // Early validation: abort if pendoKey or appEnv is missing
    const isPendoKeyMissing = !config.pendoKey || config.pendoKey.trim() === "";
    const isAppEnvMissing = !config.appEnv || config.appEnv.trim() === "";
    const isAppEnvInvalid = !isAppEnvMissing &&
        !allowedAppEnvs.includes(config.appEnv.trim());
    if (isPendoKeyMissing || isAppEnvMissing || isAppEnvInvalid) {
        if (typeof window !== "undefined") {
            const missingFields = [];
            if (isPendoKeyMissing)
                missingFields.push("pendoKey");
            if (isAppEnvMissing)
                missingFields.push("appEnv");
            if (isAppEnvInvalid) {
                // eslint-disable-next-line no-console
                console.error(`PendoProvider: Invalid appEnv value "${config.appEnv}". ` +
                    `Allowed values are: ${allowedAppEnvs.join(", ")}. ` +
                    "Pendo will not be initialized.");
            }
            else {
                // eslint-disable-next-line no-console
                console.error(`PendoProvider: Required configuration missing (${missingFields.join(", ")}). Pendo will not be initialized. ` +
                    "Please provide valid pendoKey and appEnv in the config to use Pendo.");
            }
        }
        return _jsx(_Fragment, { children: children });
    }
    return _jsx(PendoInitializer, { config: config, children: children });
};
